import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IProfessionalProfile } from 'app/interfaces/professionalprofile.interface';
import { GlobalService } from 'app/services/global.service';
import { UserService } from 'app/services/user.service';
import { ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { UnleashService } from 'shared/unleash/unleash.service';

import { MyAccountFinancialComponent } from './financial/financial.component';
import { myAccountPages } from './my-account.pages';

@Component({
	selector: 'app-my-account',
	templateUrl: './my-account.component.html',
	styleUrls: ['./my-account.component.scss'],
})
export class MyAccountComponent {
	myAccountPages = myAccountPages;
	page = myAccountPages[0];
	showMenuMobile = false;

	user: IProfessionalProfile;

	haveUnsavedChanges = false;
	showConfirmationDialog = false;
	redirectPath: string;
	public resetProfileCompletion: ReplaySubject<boolean> =
		new ReplaySubject<boolean>();
	public showHideDetails: ReplaySubject<boolean> = new ReplaySubject<boolean>();
	@ViewChild('financial') financialComponent: MyAccountFinancialComponent;

	constructor(
		public globalService: GlobalService,
		public router: Router,
		public activateRoute: ActivatedRoute,
		public userService: UserService,
		private unleashService: UnleashService
	) {
		this.showMenuMobile = this.isMobileDevice();
		this.getProfileData();
		this.handleRoute();

		this.resetProfileCompletion.next(true);
		this.showHideDetails.next(false);

		const onsiteSessionsToggle =
			this.unleashService.isEnabled('onsite-sessions');
		if (!onsiteSessionsToggle) {
			this.myAccountPages = this.myAccountPages.filter(
				(page) => page.path !== 'onsite-sessions'
			);
		}
	}

	getProfileData(): void {
		this.userService.getProfileData().then((user) => {
			this.user = user;
		});
	}

	handleRoute(): void {
		this.activateRoute.data.pipe(take(1)).subscribe((data: any) => {
			let pageName = data?.pageName || 'account';
			if (pageName == 'account' && !this.isMobileDevice()) {
				pageName = 'personal';
			}
			this.changePage(pageName);
		});
	}

	changePage(path: string): void {
		if (path === 'account') return;

		if (this.haveUnsavedChanges) {
			this.showConfirmationDialog = true;
			this.redirectPath = `/account/${path}`;
			return;
		}

		this.page =
			this.myAccountPages.find((page) => page.path === path) ||
			this.myAccountPages[0];

		if (!this.isSamePageActive(path)) {
			this.router.navigate([`/account/${path}`], {
				queryParamsHandling: 'merge',
			});
		}

		if (this.isMobileDevice()) {
			this.showMenuMobile = false;
		}
	}

	isSamePageActive(pageName: string): boolean {
		let urlPath;
		if (this.activateRoute.snapshot?.url?.length) {
			urlPath = this.activateRoute.snapshot?.url[0]?.path;
		}
		return urlPath === pageName;
	}

	updateUser(user: IProfessionalProfile): void {
		this.userService
			.getProfessionalProfileData(this.user.id)
			.subscribe((profile: IProfessionalProfile) => {
				if (profile) {
					const data: IProfessionalProfile = this.handleUserData(profile, user);
					this.userService.saveData(data);
					this.userUpdated();
					this.userService.profileUpdated.next();
				}
			});
	}

	userUpdated(): void {
		this.resetProfileCompletion.next(true);
	}

	handleUserData(
		profile: IProfessionalProfile,
		user: IProfessionalProfile
	): IProfessionalProfile {
		return {
			...profile,
			country: user.country,
			license: user.license,
			description: user.description,
			email: user.email,
			displayName: user.displayName,
			imageUrl: user.imageUrl,
			socialReason: user.socialReason,
			priceOneHour: user.priceOneHour,
			priceHalfHour: user.priceHalfHour,
			tags: user?.tags,
		};
	}

	isFinancialMobileSubPage(): boolean {
		if (this.page.path != 'payment') {
			return false;
		}
		if (this.showMenuMobile) {
			return false;
		}
		return !!this.financialComponent && !!this.financialComponent.subPageName;
	}

	isMobileDevice(): boolean {
		return this.globalService.isMobileDevice();
	}

	back(): void {
		if (!this.showMenuMobile) {
			if (!this.haveUnsavedChanges) {
				this.showMenuMobile = true;
			}
			this.router.navigate(['/account']);
			return;
		}

		this.router.navigate(['/calendar']);
	}

	continueNavigate(): void {
		this.showConfirmationDialog = false;
		this.haveUnsavedChanges = false;
		this.router.navigate([this.redirectPath || `/account/'personal`]);
	}
}
